import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import projectService from "../services/projectService";
import InnerPageLayout from "../components/Layout/InnerPageLayout";
import BreadCrumb from "../components/Breadcrumb/BreadCrumb";
import { CaseCard } from "../components/CaseStudies/CaseStudyOne";
import Pagination from "../components/Blog/Pagination";
import { useContentAvailability } from "../utils/contentAvailability";

export default function CaseStudies() {
    const { hasCaseStudies } = useContentAvailability();
    const [searchParams, setSearchParams] = useSearchParams();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: parseInt(process.env.REACT_APP_ITEMS_PER_PAGE) || 12,
        total: 0,
        totalPages: 1
    });

    useEffect(() => {
        const fetchProjects = async () => {
            setLoading(true);
            const limit = parseInt(process.env.REACT_APP_ITEMS_PER_PAGE) || 12;
            try {
                const response = await projectService.getAllProjects({
                    page: currentPage,
                    limit: limit
                });

                if (response.success || response.data) {
                    setProjects(response.data || []);
                    setPagination(response.pagination || {
                        page: currentPage,
                        limit: limit,
                        total: (response.data || []).length,
                        totalPages: 1
                    });
                } else {
                    setProjects(response || []);
                }
                setLoading(false);
            } catch (error) {
                console.error("Error fetching projects:", error);
                setError("Failed to load case studies.");
                setLoading(false);
            }
        };

        fetchProjects();
    }, [currentPage]);

    // Update URL params when page changes
    useEffect(() => {
        const params = new URLSearchParams(searchParams);
        if (currentPage > 1) {
            params.set('page', currentPage.toString());
        } else {
            params.delete('page');
        }
        setSearchParams(params, { replace: true });
    }, [currentPage, searchParams, setSearchParams]);

    // Return null if case studies are not available
    if (!hasCaseStudies) {
        return null;
    }

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <InnerPageLayout>
            <BreadCrumb
                pageTitle="Case Studies"
                items={[{ label: "Case Studies", path: "/case-studies" }]}
            />
            <section className="py-16 md:py-[130px]">
                <div className="theme-container w-full mx-auto px-4">
                    {loading ? (
                        <div className="text-center py-16">
                            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple"></div>
                            <p className="text-white mt-4">Loading case studies...</p>
                        </div>
                    ) : error ? (
                        <div className="text-center py-16">
                            <p className="text-red-500">{error}</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-6 lg:grid-cols-12 gap-5 sm:gap-[30px]">
                            {projects.length > 0 ? (
                                <>
                                    {projects.map((project, index) => (
                                        <CaseCard
                                            key={index}
                                            title={project.title}
                                            category={project.category || "Case Study"}
                                            image={project.image}
                                            description={project.overview || project.description}
                                            slug={project.slug}
                                        />
                                    ))}

                                    {/* Column span for pagination to take full width */}
                                    <div className="col-span-full flex justify-center mt-10">
                                        {pagination.totalPages > 1 && (
                                            <Pagination
                                                currentPage={pagination.page}
                                                totalPages={pagination.totalPages}
                                                onPageChange={handlePageChange}
                                            />
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div className="col-span-full text-center py-16">
                                    <p className="text-white">No case studies found.</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </section>
        </InnerPageLayout>
    );
}
